import React, { useState } from 'react';
import {
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  Box,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';

const InsightsSection: React.FC = () => {
  const [expanded, setExpanded] = useState(false);
  const navigate = useNavigate();

  const articles = [
    {
      title: "U.S. Tariffs Disrupt Global Shipping",
      slug: "us-tariffs-disrupt-global-shipping",
      date: "May 6, 2025",
      description:
        "New tariffs imposed by the U.S. government have led to a substantial decline in shipping volumes from China, affecting major ports like Los Angeles and Seattle.",
      link: "https://www.axios.com/2025/05/06/trump-tariffs-los-angeles-seattle",
      image: "/tariff.jpg",
    },
    {
      title: "US considering new ship registry in US Virgin Islands",
      slug: "us-considering-new-ship-registry-in-us-virgin-islands",
      date: "May 8, 2025",
      description:
        "The U.S. is exploring a new shipping registry based in the US Virgin Islands to strengthen maritime competitiveness.",
      link: "https://www.reuters.com/business/syria-signs-new-30-year-deal-with-french-shipping-giant-cma-cgm-2025-05-01",
      image: "/flag.jpg",
    },
    {
      title: "Green Shipping Corridors Gaining Traction",
      slug: "green-shipping-corridors-gaining-traction",
      date: "Mar 15, 2025",
      description:
        "New international green shipping corridors are being adopted to reduce emissions across trade lanes.",
      link: "#",
      image: "/green.jpg",
    },
    {
      title: "Port of Singapore Launches AI Operations Center",
      slug: "port-of-singapore-launches-ai-operations-center",
      date: "Apr 3, 2025",
      description:
        "Singapore’s port authority has launched an AI-driven control center to optimize container flow.",
      link: "#",
      image: "/singapore.jpg",
    },
    {
      title: "U.S. port fees proposal, carrier alliances shift, and congestion",
      slug: "us-port-fees-proposal-carrier-alliances-shift-and-congestion",
      date: "Mar 24, 2025",
      description:
        "New proposals for port fees and changes in carrier alliances signal a shift in the global logistics ecosystem.",
      link: "https://www.reuters.com/business/syria-signs-new-30-year-deal-with-french-shipping-giant-cma-cgm-2025-05-01",
      image: "/port.jpg",
    },
    {
      title: "Major Container Backlogs Hit Europe",
      slug: "major-container-backlogs-hit-europe",
      date: "Apr 12, 2025",
      description:
        "European ports experience significant backlogs due to labor strikes and capacity mismatches.",
      link: "#",
      image: "/backlog.jpg",
    },
    {
      title: "Panama Canal Restrictions Affect Cargo Routes",
      slug: "panama-canal-restrictions-affect-cargo-routes",
      date: "Feb 20, 2025",
      description:
        "Drought-related limits in Panama Canal traffic force shipping companies to reroute vessels.",
      link: "#",
      image: "/panama.jpg",
    },
    {
      title: "Syria Renews Port Contract with CMA CGM",
      slug: "syria-renews-port-contract-with-cma-cgm",
      date: "Feb 5, 2025",
      description:
        "Syria signs a long-term agreement with CMA CGM to upgrade and operate the Latakia port.",
      link: "https://www.marketwatch.com/story/cargo-giant-expeditors-sees-china-to-u-s-shipping-declining-significantly-28a111d8",
      image: "/cma.jpg",
    },
  ];

  // Show all articles except the first by default (you can adjust as needed)
  const visibleArticles = expanded ? articles.slice(1) : articles.slice(1, 4);

  return (
    <Box
      id='insights'
      sx={{
        backgroundColor: '#f9f9f9',
        py: 10,
        px: { xs: 1, sm: 2, md: 4, lg: 10, xl: 18 }, // Responsive padding for large screens
      }}
    >
      <Box sx={{ maxWidth: { lg: '100%',xs: '100%', xl: '100%' }, mx: 'auto' }}>
        <Typography
          variant="h4"
          align="center"
          fontWeight={700}
          mb={6}
          fontFamily={"'Playfair Display', Georgia, serif"}
        >
          Featured Insights
        </Typography>

        {/* Featured Card (first one, not in load more) */}
        <Card
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            mb: 6,
            height: { md: 300 },
          }}
        >
          <CardMedia
            component="img"
            image="/container.jpg"
            alt="Featured Insight"
            sx={{ width: { md: '50%' }, height: 300, objectFit: 'cover' }}
          />
          <CardContent
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Navigating Global Shipping Challenges
            </Typography>
            <Typography variant="body1" color="text.primary">
              Explore how leaders in logistics are overcoming supply chain disruption with strategic agility and technological innovation.
            </Typography>
          </CardContent>
        </Card>

        {/* Article Cards */}
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 4,
            justifyContent: 'space-between',
            pt: 3,
          }}
        >
          {visibleArticles.map((article, idx) => (
            <Card
              key={idx}
              onClick={() => {
                navigate(`/news/${article.slug}`);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              sx={{
                width: {
                  xs: '100%',
                  sm: '48%',
                  md: '31%',
                  lg: '31%',
                  xl: '31%', // Take 90% of width on xl
                },
                display: 'flex',
                flexDirection: 'column',
                cursor: 'pointer',
                mx: { xl: 'auto' }, // center cards in row if 90%
              }}
            >
              <CardMedia
                component="img"
                image={article.image}
                alt={article.title}
                sx={{ height: 180, objectFit: 'cover' }}
              />
              <CardContent>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {article.title}
                </Typography>
                <Typography variant="body2" color="text.primary">
                  {article.description}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>

        <Box display="flex" justifyContent="center" mt={6}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? 'See Fewer Articles' : 'Load More'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default InsightsSection;
